import {
  Box,
  Button,
  Flex,
  HStack,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import loadable from '@loadable/component';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import {
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { useAppDispatch, useTypedSelector } from '../../hooks/store';
import useDomain from '../../hooks/useDomain';
import SkytrakBackground from '../../images/background/background-skytrak.jpeg';
import Background from '../../images/background/login-background.jpg';
import {
  selectIsSkytrakApp,
  selectIsSkytrakIOS,
  selectIsTabletApp,
} from '../../slices/config';
import { selectLocale } from '../../slices/language';
import {
  fetchPublicConfigurationData,
  getLocationIPUser,
  selectGolftecTerms,
  selectLocationUserDataByIP,
} from '../../slices/loadConfiguration';
import {
  fetchUserData,
  selectAuthenticationStatus,
  selectLoginByTicket,
  selectUserData,
  signInWithChildrenAccount,
  signOut,
} from '../../slices/user';
import ModalShowError from '../ModalShowError';

const PrivacyTerms = loadable(() => import('../PrivacyTermsOfService'));
const CookiePolicies = loadable(
  () => import('../../components/CookiePolicies')
);

const PublicRoute = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const domain = useDomain();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const subAccount = searchParams.get('sub-account');
  const parentToken = searchParams.get('parentToken');
  const [isAuthenticated, userData, locale, golftecTerms, loginByTicket] = [
    useTypedSelector(selectAuthenticationStatus),
    useTypedSelector(selectUserData),
    useTypedSelector(selectLocale),
    useTypedSelector(selectGolftecTerms),
    useTypedSelector(selectLoginByTicket),
  ];
  const { isOpen, onOpen, onClose } = useDisclosure();
  const childrenToken = sessionStorage.getItem('childrenToken');
  const isTabletApp = useTypedSelector(selectIsTabletApp);
  const isSkytrakApp = useTypedSelector(selectIsSkytrakApp);
  const isSkytrakIOS = useTypedSelector(selectIsSkytrakIOS);
  const locationUserDataByIP = useTypedSelector(selectLocationUserDataByIP);
  const { search, pathname } = useLocation();

  useEffect(() => {
    if (locale && !golftecTerms) {
      dispatch(fetchPublicConfigurationData());
    }
  }, [dispatch, golftecTerms, locale]);

  useEffect(() => {
    localStorage.removeItem('esn');
  }, []);

  useEffect(() => {
    if (domain === 'skytrak') {
      if (!locationUserDataByIP) {
        dispatch(getLocationIPUser());
      } else if (
        window.location.hostname.match(/skytrakgolf.com/) &&
        locationUserDataByIP.countryCode === 'JP'
      ) {
        if (pathname.match(/sign-up|register/)) {
          console.log(pathname);
          let newUrl = window.location.hostname.replace('.com', '.jp');
          newUrl = newUrl + pathname + search;
          window.location.replace(newUrl);
        }
      }
    }
  }, [dispatch, domain, locationUserDataByIP, pathname, search]);

  useEffect(() => {
    if (subAccount) {
      dispatch(signInWithChildrenAccount({ username: subAccount }))
        .unwrap()
        .then(() => {
          dispatch(fetchUserData());
          navigate(location.pathname, { replace: true });
        });
    }
  }, [dispatch, location.pathname, navigate, subAccount, parentToken]);

  useEffect(() => {
    const esnParams = search.replace('?', '');
    const esn = localStorage.getItem('esn');
    if (isAuthenticated) {
      let registerLogin = localStorage.getItem('registerLogin');

      if (userData) {
        if (!userData?.isForTest) {
          let isAuthorized = true;
          if (userData.account) {
            if (!userData.ageAffirmation)
              isAuthorized = !!(userData.age && parseInt(userData.age) >= 18);
          } else if (!userData.consent) {
            if (!userData.ageAffirmation)
              isAuthorized = !!(userData.age && parseInt(userData.age) >= 18);
          }
          if (
            userData.wuci === userData.account &&
            !userData.birthday &&
            !userData.ageAffirmation
          ) {
            navigate('/age-affirmation');
          } else if (!isAuthorized) {
            navigate('/children-privacy');
          } else if (userData.needVerifyAccount || userData.needChangeEmail) {
            navigate('/verify-email');
          } else if (userData.readTermOfUse) {
            navigate('/terms-and-conditions');
          } else if (pathname.match(/forgot-password/)) {
            dispatch(signOut());
          } else if (pathname.match(/register/)) {
            if (
              (esnParams && esnParams.match('XXXX')) ||
              (esn && esn.match('XXXX'))
            ) {
              dispatch(signOut());
              navigate('/sign-up');
            } else {
              if (!registerLogin) {
                onOpen();
              } else {
                localStorage.removeItem('registerLogin');
                if (esnParams) {
                  if (!esn) {
                    localStorage.setItem('esn', esnParams.replace('esn=', ''));
                  }
                  navigate('/my-device');
                } else {
                  navigate('/');
                }
              }
            }
          } else {
            navigate('/');
          }
        } else if (pathname.match(/forgot-password/)) {
          dispatch(signOut());
        } else if (pathname.match(/register/)) {
          if (
            (esnParams && esnParams.match('XXXX')) ||
            (esn && esn.match('XXXX'))
          ) {
            dispatch(signOut());
            navigate('/sign-up');
          } else {
            if (!registerLogin) {
              onOpen();
            } else {
              localStorage.removeItem('registerLogin');
              if (esnParams) {
                if (!esn) {
                  localStorage.setItem('esn', esnParams.replace('esn=', ''));
                }
                navigate('/my-device');
              } else {
                navigate('/');
              }
            }
          }
        } else {
          navigate('/');
        }
      }
    } else {
      if (
        pathname.match(/\/share/i) ||
        pathname.match(/\/change-password/i) ||
        pathname.match(/\/verify-email/i) ||
        pathname.match(/\/age-affirmation/i) ||
        pathname.match(/\/children-privacy/i) ||
        pathname.match(/\/terms-and-conditions/i)
      ) {
        navigate('/login');
      } else if (pathname.match(/register/)) {
        if (
          (esnParams && esnParams.match('XXXX')) ||
          (esn && esn.match('XXXX'))
        ) {
          dispatch(signOut());
          navigate('/sign-up');
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    userData,
    subAccount,
    loginByTicket,
    childrenToken,
    pathname,
    search,
  ]);

  return (
    <Flex
      alignItems={`center`}
      justifyContent={`center`}
      bgColor={`#54565B`}
      w={`100%`}
      minH={
        'calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom))'
      }
      bgImg={` linear-gradient(rgba(0, 0, 0, 0.2) 100%, rgba(0, 0, 0, 0.2) 100%),url(${
        domain === 'skytrak' ? SkytrakBackground : Background
      })`}
      bgRepeat={'no-repeat'}
      bgSize={'cover'}
      p={{ base: '70px 16px', sm: '0px' }}
    >
      <Flex
        alignItems={`center`}
        justifyContent={`center`}
        padding={{ md: '80px 0px', xl: '0px' }}
        sx={{
          '::-webkit-scrollbar': {
            display: 'none',
          },
        }}
        overflowX={{ md: 'hidden', xl: 'unset' }}
        overflowY={{ md: 'auto', xl: 'unset' }}
      >
        <Outlet />
        <PrivacyTerms />
        {!isTabletApp && !isSkytrakApp && !isSkytrakIOS && <CookiePolicies />}
        <ModalShowError isOpen={isOpen} onClose={onClose} maxWidth={360}>
          {(() => {
            return (
              <VStack spacing={'16px'}>
                <Box
                  fontWeight={'bold'}
                  fontSize={'17px'}
                  lineHeight={'20px'}
                  letterSpacing={'0.25px'}
                  textTransform={'uppercase'}
                  color={'#54565b'}
                >
                  {intl.formatMessage({ id: 'NoticeServer' })}
                </Box>

                <Box
                  fontSize={'15px'}
                  lineHeight={'20px'}
                  letterSpacing={'0.25px'}
                  color={'#54565B'}
                  textAlign={'center'}
                >
                  {intl.formatMessage({ id: 'WarningCurrentUser' })}
                </Box>
                <HStack
                  spacing={'16px'}
                  width={'100%'}
                  marginTop={'32px !important'}
                >
                  <Button
                    variant={'tertiarySkyTrak'}
                    width={'100%'}
                    textTransform={'uppercase'}
                    onClick={() => {
                      onClose();
                      if (pathname.match(/register/)) {
                        const esnParams = search.replace('?', '');
                        localStorage.removeItem('registerLogin');
                        const esn = localStorage.getItem('esn');

                        if (esnParams) {
                          if (!esn) {
                            localStorage.setItem(
                              'esn',
                              esnParams.replace('esn=', '')
                            );
                          }
                          navigate('/my-device');
                        } else {
                          navigate('/');
                        }
                      } else {
                        navigate('/');
                      }
                    }}
                  >
                    {intl.formatMessage({ id: 'No' })}
                  </Button>
                  <Button
                    variant={'secondarySkyTrak'}
                    width={'100%'}
                    onClick={() => {
                      dispatch(signOut());
                      onClose();
                      if (pathname.match(/register/)) {
                        const esnParams = search.replace('?', '');
                        if (esnParams) {
                          localStorage.setItem(
                            'esn',
                            esnParams.replace('esn=', '')
                          );
                        }
                      }
                    }}
                  >
                    {intl.formatMessage({ id: 'Yes' })}
                  </Button>
                </HStack>
              </VStack>
            );
          })()}
        </ModalShowError>
      </Flex>
    </Flex>
  );
};

export default PublicRoute;
